import { Code, ConnectError } from "@connectrpc/connect";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "@viajero/shared-ui/analytics";
import { useRequestEmailVerification } from "@/features/reservation/api/useRequestEmailVerification";
import { useVerifyEmailCode } from "@/features/reservation/api/useVerifyEmailCode";
import { isValidEmail } from "@/lib/isValidEmail";
import { colors } from "@/theme/colors";

const VERIFICATION_CODE_LENGTH = 6;
// 同一案件への再送抑止は backend が 60 秒で判定する (§5.1)。UI 側も同じ間隔でボタンを
// 抑止し、無駄な ResourceExhausted を避ける。
const RESEND_COOLDOWN_MS = 60_000;

type Props = {
  languageCode: string;
  email: string;
  verified: boolean;
  // 予約内容 (日時・枚数・同意) が有効か (親で isBookingSelectionValid 判定)。認証自体は予約内容に
  // 依存しないが、入力の流れを変えないよう予約内容が揃うまで認証を始めさせない。
  bookingValid: boolean;
  onVerified: () => void;
};

/**
 * メールアドレス認証ボタン。
 *
 * 「メールアドレス認証」押下で RequestEmailVerification を呼んで 6 桁コードを送信し、Dialog を
 * 開く。認証は案件 (x-case-token) に紐付き、予約 ID は不要 (予約は「決済に進む」で作成する。
 * JPTP_CANSEI-1678)。Dialog では入力コードを VerifyEmailCode で照合し、成功で認証済み状態へ
 * 遷移させる (§6.4)。コード再送は 60 秒抑止する。
 *
 * メールアドレスを変更すると親 (BuyerInfoForm) が emailVerified を false に戻すため、
 * 本コンポーネントも email 変更時に発行済みの認証 (verificationId) を破棄して再認証させる。
 */
export function EmailVerificationButton({
  languageCode,
  email,
  verified,
  bookingValid,
  onVerified,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // 1 文字ずつの入力欄 (VERIFICATION_CODE_LENGTH 個) で認証コードを保持する。
  const [digits, setDigits] = useState<string[]>(Array(VERIFICATION_CODE_LENGTH).fill(""));
  const [verificationId, setVerificationId] = useState<string | null>(null);
  // verificationId を発行した時点のメールアドレス。email 変更後の再開で古いコードを
  // 使い回して「別メールの認証で verified 扱いになる」のを防ぐ照合に使う (§6.4)。
  const [issuedEmail, setIssuedEmail] = useState<string | null>(null);
  // 言語切替に追従できるよう、state には翻訳済み文字列ではなく i18n キーを保持する。
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const requestVerification = useRequestEmailVerification();
  const verifyCode = useVerifyEmailCode();

  const issuing = requestVerification.isPending;
  const code = digits.join("");

  // 再送クールダウンの残り秒数を更新する (Dialog 表示中のみ)。
  // ペイント前に同期実行し、開いた直後の 1 フレームで secondsLeft が 0 のままになり
  // 再送ボタンが一瞬有効化される (バックエンド抑止に当たる) のを防ぐ。
  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    const update = () =>
      setSecondsLeft(Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000)));
    update();
    const id = window.setInterval(update, 500);
    return () => window.clearInterval(id);
  }, [open, cooldownUntil]);

  if (verified) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CheckCircleIcon sx={{ color: colors.success, fontSize: 20 }} />
        <Typography sx={{ color: colors.success, fontSize: 14, fontWeight: 600 }}>
          {t("buyerInfo.emailVerified")}
        </Typography>
      </Box>
    );
  }

  const keyForIssueError = (err: unknown): string => {
    if (err instanceof ConnectError) {
      switch (err.code) {
        case Code.ResourceExhausted:
          return "buyerInfo.verifyErrorResendTooSoon";
        case Code.Unavailable:
          return "buyerInfo.verifyErrorSendFailed";
        case Code.NotFound:
          return "buyerInfo.verifyErrorNotFound";
      }
    }
    return "buyerInfo.verifyErrorGeneric";
  };

  const keyForVerifyError = (err: unknown): string => {
    if (err instanceof ConnectError) {
      switch (err.code) {
        case Code.InvalidArgument:
          return "buyerInfo.verifyErrorCodeMismatch";
        // 試行 5 回超過による無効化・期限切れ (5 分) はいずれも再発行が必要 (§6.4)。
        case Code.FailedPrecondition:
          return "buyerInfo.verifyErrorExpired";
        case Code.NotFound:
          return "buyerInfo.verifyErrorNotFound";
      }
    }
    return "buyerInfo.verifyErrorGeneric";
  };

  // 認証コード発行 → Dialog 表示。再送時も同じ経路を通る。
  const issueCode = async () => {
    setErrorKey(null);
    try {
      const issued = await requestVerification.mutateAsync({ email, languageCode });
      setVerificationId(issued.verificationId);
      setIssuedEmail(email);
      setDigits(Array(VERIFICATION_CODE_LENGTH).fill(""));
      setCooldownUntil(Date.now() + RESEND_COOLDOWN_MS);
      setOpen(true);
    } catch (err) {
      // backend の 60 秒再送抑止 (ResourceExhausted) に当たった場合も UI クールダウンを
      // 開始し、再送ボタンの連打で同じエラーを繰り返すのを防ぐ。
      if (err instanceof ConnectError && err.code === Code.ResourceExhausted) {
        setCooldownUntil(Date.now() + RESEND_COOLDOWN_MS);
      }
      setErrorKey(keyForIssueError(err));
    }
  };

  const handleOpen = () => {
    // メールアドレスは個人情報のため送らない (JPTP_CANSEI-1400 / スプレッドシートのメモに準拠)。
    trackEvent("click_verify_email_start");
    setErrorKey(null);
    // 発行済みコードが同じメール向けなら再送せず Dialog を開き直す
    // (開き直しのたびに再送すると 60 秒抑止に阻まれるため)。メール変更後は再発行する。
    if (verificationId && issuedEmail === email) {
      setOpen(true);
      return;
    }
    void issueCode();
  };

  const handleVerify = async () => {
    trackEvent("click_submit_email_code");
    if (!verificationId) {
      return;
    }
    setErrorKey(null);
    try {
      await verifyCode.mutateAsync({ verificationId, code });
      setOpen(false);
      onVerified();
    } catch (err) {
      setErrorKey(keyForVerifyError(err));
      setDigits(Array(VERIFICATION_CODE_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    }
  };

  const handleDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    // 数字が入力されたら次の入力欄へフォーカスを移す。
    if (digit && index < VERIFICATION_CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLDivElement>) => {
    // 空欄で Backspace が押されたら前の入力欄へ戻る。
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // メール等からコピーした認証コードの貼り付けに対応する。1 桁ずつ入力欄が分かれていても
  // 貼り付けた数字を貼り付け位置 (index) から順に各欄へ流し込み、最後に埋めた欄の次
  // (無ければ末尾) へフォーカスを移す。数字以外は除去する。
  const handlePaste = (index: number, e: React.ClipboardEvent<HTMLDivElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) {
      return;
    }
    e.preventDefault();
    const next = [...digits];
    for (let i = 0; i < pasted.length && index + i < VERIFICATION_CODE_LENGTH; i++) {
      // ループ条件 (i < pasted.length) で桁は必ず存在するが、noUncheckedIndexedAccess 下では
      // string | undefined になるためフォールバックを添える。
      next[index + i] = pasted[i] ?? "";
    }
    setDigits(next);
    const filledCount = Math.min(pasted.length, VERIFICATION_CODE_LENGTH - index);
    const focusIndex = Math.min(index + filledCount, VERIFICATION_CODE_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  // 照合中 (verifyCode.isPending) の再送は verificationId 上書きなどの競合を招くため抑止する。
  const resendDisabled = issuing || secondsLeft > 0 || verifyCode.isPending;

  return (
    <Box>
      <Button
        variant="outlined"
        disabled={!isValidEmail(email) || !bookingValid || issuing}
        onClick={handleOpen}
        sx={{ width: { xs: "100%", md: "auto" }, minHeight: 48 }}
      >
        {issuing ? <CircularProgress size={20} /> : t("buyerInfo.verifyEmailButton")}
      </Button>
      <Typography sx={{ color: colors.textMuted, fontSize: 12, mt: 0.5 }}>
        {t("buyerInfo.verifyEmailHint")}
      </Typography>
      {!open && errorKey && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {t(errorKey)}
        </Alert>
      )}

      <Dialog
        open={open}
        onClose={(_e, reason) => reason !== "backdropClick" && setOpen(false)}
        slotProps={{ paper: { sx: { p: 2 } } }}
      >
        <IconButton
          onClick={() => setOpen(false)}
          aria-label={t("buyerInfo.verifyEmailDialogCloseLabel")}
          sx={{ position: "absolute", top: 8, right: 8, color: colors.textMuted }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle>{t("buyerInfo.verifyEmailDialogTitle")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: colors.textBody, mb: 2 }}>
            {t("buyerInfo.verifyEmailDescription")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
            {digits.map((digit, index) => (
              <TextField
                // biome-ignore lint/suspicious/noArrayIndexKey: 固定長・並び替えのない入力欄のため index を key に使用
                key={index}
                inputRef={(el) => {
                  inputRefs.current[index] = el;
                }}
                value={digit}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={(e) => handlePaste(index, e)}
                autoFocus={index === 0}
                slotProps={{
                  htmlInput: {
                    inputMode: "numeric",
                    maxLength: 1,
                    "aria-label": t("buyerInfo.verificationCodeInputLabel", {
                      index: index + 1,
                      total: VERIFICATION_CODE_LENGTH,
                    }),
                    style: { textAlign: "center", fontSize: 20, padding: "8px 0" },
                  },
                }}
                sx={{ width: 44 }}
              />
            ))}
          </Box>
          {errorKey && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t(errorKey)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions
          disableSpacing
          sx={{ flexDirection: "column", alignItems: "stretch", gap: 1 }}
        >
          <Button
            variant="outlined"
            fullWidth
            disabled={resendDisabled}
            onClick={() => void issueCode()}
          >
            {secondsLeft > 0
              ? t("buyerInfo.resendCooldown", { seconds: secondsLeft })
              : t("buyerInfo.resend")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            disabled={code.length !== VERIFICATION_CODE_LENGTH || verifyCode.isPending || issuing}
            onClick={() => void handleVerify()}
          >
            {verifyCode.isPending ? <CircularProgress size={20} /> : t("buyerInfo.verify")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
